import { useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { mqLargeMin } from 'styled/tools/media-queries';
import Accordion from 'components/library/Accordion/accordion';
import Icon from 'components/global/Icon/Primary';
import ClinicalTrials from 'components/pdp/clinical-trials';
import DSStrains from 'components/pdp/ds01/ds-strains';
import { imageUrl, richTextToHtml } from 'components/pdp/modules/helpers';
import type {
  ContentImage,
  StrainsModule,
  TransformativeModule,
} from 'components/pdp/modules/types';

const TransformWrapper = styled.div`
  padding: 3.5rem 1rem;
  background-color: var(--fill-secondary);

  @media ${mqLargeMin} {
    padding: 6rem 3.5rem;
  }

  * {
    color: var(--fill-brand);
  }
`;

const TransformContentsWrapper = styled.div`
  margin: auto;
  max-width: 1440px;
`;

const TitleWrapper = styled.div`
  margin-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;

  @media ${mqLargeMin} {
    margin-bottom: 5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Title = styled.h2`
  max-width: 39rem;
  text-wrap: auto;
  font-size: 32px;
  font-weight: 400;
  letter-spacing: -0.96px;
  line-height: 35.2px;

  @media ${mqLargeMin} {
    font-size: 48px;
    line-height: 56px;
  }
`;

const TransformContents = styled.div`
  @media ${mqLargeMin} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .accordion-title {
    padding-right: 1rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-4);
    font-weight: 500;
    font-size: 18px;

    @media ${mqLargeMin} {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0;
    }
  }

  .accordion-title .afu-count {
    font-family: 'Source Code Pro', 'Fira Code', monospace;
    font-size: 14px;
    text-transform: uppercase;
  }

  .title-full-width,
  .title-full-width p {
    width: 100%;
  }

  .transform-contents-inner {
    .accordion-p:first-of-type {
      margin-top: 1rem;
      margin-bottom: 1rem;
    }

    .accordion-p:not(:first-of-type) {
      margin-bottom: 8px;
      font-size: 14px;
    }

    .accordion-p em,
    .accordion-p strong {
      font-size: inherit;
    }

    em {
      font-style: italic;
    }
  }
`;

const IconGrid = styled.div`
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 0.75rem;
  row-gap: 1rem;

  @media ${mqLargeMin} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const BenefitIconImageFrame = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  max-height: 40px;
`;

const TransformativeIllustrationFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  align-self: start;
`;

const TRANSFORMATIVE_SECTION_TITLE_FALLBACK =
  '24 strain formula for transformative gut benefits*';

const TRANSFORMATIVE_SECTION_IMAGE_FALLBACK =
  'https://assets.embeddables.com/leftcontent41_7513800312465626.png';

const OpenTrialsButton = styled.button`
  appearance: none;
  display: inline-flex;
  margin-top: 1rem;
  border: 0;
  align-items: center;
  gap: 0.5rem;
  text-decoration: underline;
  font-weight: 500;
  color: var(--fill-brand);
  background-color: transparent;
  cursor: pointer;
`;

type ProductTransformativeProductInfo = Record<string, unknown> & {
  title?: string;
  shortTitle?: string;
};

interface ProductTransformativeProps {
  transformativeModule?: TransformativeModule;
  strainsModule?: StrainsModule;
  productInfo: ProductTransformativeProductInfo;
}

const getTransformativeSectionImage = (
  image: ContentImage | undefined,
  productInfo: ProductTransformativeProductInfo,
): { src: string; alt: string } => {
  const src = image?.url?.trim() || TRANSFORMATIVE_SECTION_IMAGE_FALLBACK;

  const alt =
    image?.alt?.trim() ||
    (productInfo.shortTitle || productInfo.title
      ? `Illustration — ${productInfo.shortTitle || productInfo.title}`
      : 'Strains and supplement illustration');

  return { src, alt };
};

const ProductTransformative = ({
  transformativeModule,
  strainsModule,
  productInfo,
}: ProductTransformativeProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (
    !transformativeModule?.benefitFaqs?.length ||
    !transformativeModule?.icons?.length ||
    !productInfo
  ) {
    return null;
  }

  const { benefitFaqs, icons } = transformativeModule;

  const resolvedTransformativeImage = getTransformativeSectionImage(
    transformativeModule.image,
    productInfo,
  );

  const accordionContent = benefitFaqs.map((benefit) => {
    return {
      title: `<span class="accordion-title">${benefit.title ?? ''} <span class="afu-count">${benefit.subtitle ?? ''}</span></span>`,
      content: richTextToHtml(benefit.content, {
        newLineToBreak: true,
        classes: {
          p: 'accordion-p',
        },
      }),
      accordionEvent: {
        buttonName: 'FAQ',
        question: benefit.title,
        location: 'PDP',
      },
    };
  });

  return (
    <TransformWrapper>
      <TransformContentsWrapper>
        <TitleWrapper>
          <Title>
            {transformativeModule.title?.trim() ||
              TRANSFORMATIVE_SECTION_TITLE_FALLBACK}
          </Title>
          <OpenTrialsButton onClick={() => setIsOpen(true)}>
            View Strains + Ingredients
            <Icon name='arrow-right' size='md' />
          </OpenTrialsButton>
          <ClinicalTrials
            productInfo={productInfo}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contents=''
          >
            <DSStrains strainsModule={strainsModule} icons={icons} />
          </ClinicalTrials>
        </TitleWrapper>
        <TransformContents>
          <TransformativeIllustrationFrame>
            <Image
              src={resolvedTransformativeImage.src}
              alt={resolvedTransformativeImage.alt}
              fill
              sizes='(max-width: 1023px) 100vw, 50vw'
              style={{ objectFit: 'contain' }}
              priority
            />
          </TransformativeIllustrationFrame>
          <div className='transform-contents-inner'>
            <Accordion
              items={accordionContent}
              displayContentAsHTML={true}
              displayTitleAsHTML={true}
              initialItem={-1}
              titleInnerExtraClasses={'title-full-width'}
            />
            <IconGrid>
              {icons.map((icon) => {
                const src = imageUrl(icon.icon, 'thumbnail');
                if (!src) {
                  return null;
                }
                return (
                  <IconWrapper key={icon._key}>
                    <BenefitIconImageFrame>
                      <Image
                        src={src}
                        alt={icon.icon?.alt ?? ''}
                        fill
                        sizes='40px'
                        style={{ objectFit: 'contain' }}
                      />
                    </BenefitIconImageFrame>
                    {icon.title}
                  </IconWrapper>
                );
              })}
            </IconGrid>
          </div>
        </TransformContents>
      </TransformContentsWrapper>
    </TransformWrapper>
  );
};

export default ProductTransformative;
